import React, { useEffect, useState } from "react";
import styles from "./AboutUs.module.css";
import {
  getAboutData,
  AboutData,
  DynamicZoneBlock,
  MemberImageInput,
} from "../../lib/getAbout";
import DOMPurify from "dompurify";

const AboutUs: React.FC = () => {
  const [aboutData, setAboutData] = useState<AboutData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const data = await getAboutData();
        setAboutData(data);
      } catch (err) {
        setError(
          "Error al cargar la información. Por favor, intente nuevamente."
        );
        console.error("Error fetching about data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, []);

  if (loading) {
    return <div className={styles.loading}>Cargando...</div>;
  }

  if (error || !aboutData) {
    return (
      <div className={styles.error}>
        {error || "No se pudo cargar la información."}
      </div>
    );
  }

  const renderDynamicBlock = (block: DynamicZoneBlock) => {
    switch (block.__component) {
      case "web-components.title-text-image":
        return (
          <section key={block.id} className={styles.companySection}>
            <h2>{block.title}</h2>
            <div
              className={styles.storyContainer}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(block.text),
              }}
            />
          </section>
        );

      case "web-components.members-image":
        return (
          <section key={block.id} className={styles.teamSection}>
            <h2>{block.title}</h2>
            <div
              className={styles.storyContainer}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(block.text),
              }}
            />
            <div className={styles.teamMembers}>
              {block.members.map((member: MemberImageInput) => {
                // Handle both possible image shapes
                let imageUrl = "";
                if (member.image) {
                  if (
                    "url" in member.image &&
                    typeof member.image.url === "string"
                  ) {
                    imageUrl = member.image.url;
                  } else if (
                    "data" in member.image &&
                    member.image.data &&
                    "attributes" in member.image.data &&
                    member.image.data.attributes &&
                    typeof member.image.data.attributes.url === "string"
                  ) {
                    imageUrl = member.image.data.attributes.url;
                  }
                }

                return (
                  <div key={member.id} className={styles.teamMember}>
                    <div
                      className={styles.teamMemberImage}
                      style={
                        imageUrl
                          ? {
                              backgroundImage: `url(${imageUrl})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : undefined
                      }
                    ></div>
                    <h3>{member.name}</h3>
                    <p>{member.position}</p>
                  </div>
                );
              })}
            </div>
          </section>
        );

      case "general.media":
        return (
          <div key={block.id} className={styles.mediaContainer}>
            {block.media?.data && (
              <img
                src={block.media.data.attributes.url}
                alt={block.media.data.attributes.alternativeText || ""}
                className={styles.mediaImage}
              />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.aboutUsContainer}>
      <div className={styles.heroSection}>
        <h1>{aboutData.title}</h1>
        <p>{aboutData.tagline}</p>
      </div>

      <div className={styles.contentSection}>
        {aboutData.blocks.map((block: DynamicZoneBlock, index: number) => (
          <React.Fragment key={block.id || index}>
            {renderDynamicBlock(block)}
            {index < aboutData.blocks.length - 1 && (
              <div className={styles.divider}></div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default AboutUs;
